import type { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('mp-panel overflow-hidden', className)} {...rest} />;
}

export function CardHeader({ title, description, actions, icon, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex flex-wrap items-start justify-between gap-3 px-6 pt-6', className)}>
      <div className="flex min-w-0 items-start gap-3.5">
        {icon && (
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] text-primary-ink" style={{ background: 'linear-gradient(135deg, rgb(var(--primary-soft)), rgb(var(--primary) / 0.14))', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)' }}>
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-[0.9375rem] font-semibold leading-snug text-ink">{title}</h2>
          {description && <p className="mt-1 text-[0.8125rem] leading-relaxed text-muted">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('p-6', className)} {...rest} />;
}

/** Thin divider row inside a card (between header and body, or between sections). */
export function CardDivider({ className }: { className?: string }) {
  return <div className={clsx('mx-6 h-px bg-line/70', className)} />;
}

const HEADER_TONE = {
  primary: 'bg-primary text-primary-fg shadow-primary',
  lime: 'mp-pill-lime',
  yellow: 'mp-pill-yellow',
  lavender: 'mp-pill-lavender',
  salmon: 'mp-pill-salmon',
  sky: 'mp-pill-sky',
  mint: 'mp-pill-mint',
} as const;

export function PageHeader({ title, description, actions, icon, tone = 'primary' }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; icon?: ReactNode; tone?: keyof typeof HEADER_TONE }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="flex items-center gap-3">
        {icon && <div className={clsx('flex h-11 w-11 items-center justify-center rounded-2xl', HEADER_TONE[tone])}>{icon}</div>}
        <div>
          <h1 className="text-xl font-semibold text-ink">{title}</h1>
          {description && <p className="mt-0.5 max-w-2xl text-[0.8125rem] text-muted">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Standard scrollable page container for non-inbox pages. */
export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className={clsx('mx-auto px-4 py-5 pb-24 md:px-6 md:py-6 md:pb-8', wide ? 'max-w-7xl' : 'max-w-5xl', className)}>{children}</div>
    </div>
  );
}
